import { forwardRef, useEffect, useState } from "react";
import styles from "./UserDetails.module.css";
import { FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-smart-toaster";
import { AddLogCallAPI } from "../../../components/AddLogs";
import PhoneInput, { parsePhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";

// ─── Constants ────────────────────────────────────────────────────────────────
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// ─── Helper: parse "HH:MM AM/PM" or "HH:MM" string → dayjs object ────────────
function parseToDayjs(timeStr) {
  if (!timeStr) return null;
  // Try "08:00 AM" / "05:00 PM"
  const m12 = timeStr.match(/^(\d{1,2}):(\d{2})\s?(AM|PM)$/i);
  if (m12) {
    let h = parseInt(m12[1], 10);
    const min = parseInt(m12[2], 10);
    const period = m12[3].toUpperCase();
    if (period === "PM" && h !== 12) h += 12;
    if (period === "AM" && h === 12) h = 0;
    return dayjs().hour(h).minute(min).second(0);
  }
  // Try "08:00" 24-hr
  const m24 = timeStr.match(/^(\d{1,2}):(\d{2})$/);
  if (m24) {
    return dayjs().hour(parseInt(m24[1], 10)).minute(parseInt(m24[2], 10)).second(0);
  }
  return null;
}

// ─── Helper: dayjs → "HH:MM AM/PM" string ─────────────────────────────────────
function dayjsTo12h(dj) {
  if (!dj || !dj.isValid()) return "";
  return dj.format("hh:mm A");
}

// ─── Helper: map raw API user → form state ────────────────────────────────────
function mapApiToForm(item) {
  if (!item) return {};

  // Split name into first / last
  const nameParts = (item.name || "").trim().split(" ");
  const firstName = nameParts[0] || "";
  const lastName = nameParts.slice(1).join(" ") || "";

  // working_days_arr = ["Mon","Wed","Fri"] → indices into DAYS
  const workDays = (item.working_days_arr || [])
    .map((d) => DAYS.indexOf(d))
    .filter((i) => i !== -1);

  return {
    id: item.id,
    userId: item.user_id,
    appUserId: item.app_user_id,
    firstName,
    lastName,
    phone: item.phone ? item.isd_code + item.phone : "",
    email: item.email || "",
    roleId: item.role_id || "",
    role: item.role || "",
    propertyIds: item.property_ids || [],
    propertyName: item.property_arr?.[0] || "",
    hrEnabled: item.is_hr_mode === 1,
    workDays,
    shiftStart: parseToDayjs(item.checkin_time),
    shiftEnd: parseToDayjs(item.checkout_time),
    clockInReminder: item.checkin_reminder || "15 minutes",
    clockOutAlert: item.clockout_reminder || "30 minutes",
    lunchTime: item.lunch_time || "",
    wageType: item.wage_type || "",
    wageAmount: item.wage || "",
    otWageType: item.ot_wage_type || "",
    otWageAmount: item.ot_wage || "",
    verified: item.is_verified === 1,
    isArchived: item.is_archived === 1,
    userImageUrl: item.user_image_url || "",
    initial: item.initial || "",
    isReceiveEmergencyCall: item.is_receive_emergency_call ?? 0,
    isReceiveTenantEmergencyCall: item.is_receive_tenant_emergency_call ?? 0,
    prevRoleId: item.role_id || "",
  };
}

const PhoneTextField = forwardRef(
  ({ error, helperText, ...props }, ref) => (
    <TextField
      {...props}
      inputRef={ref}
      fullWidth
      label="Phone (SMS clock-in)*"
      error={error}
      helperText={helperText}
    />
  )
);

// ─── Component ────────────────────────────────────────────────────────────────
export default function UserDetails({
  selectedUser,        // raw API item passed from UserList
  CloseTaskDetails,    // close the panel
  onUserUpdate,        // callback → updates parent's allData
  onArchive,           // callback → opens archive confirm in parent
  onSendVerification,  // callback → calls sendVerificationLink(id)
  propertyList = [],   // from parent: [{ value, label }]
  roleList = [],       // from parent: [{ id, name }]
}) {
  const { getAccessTokenSilently } = useAuth0();
  const [activeTab, setActiveTab] = useState("profile");
  const [form, setForm] = useState({});
  const [saveState, setSaveState] = useState("idle"); // "idle" | "saving" | "saved"
  const [errors, setErrors] = useState({});

  // Re-initialise form whenever selectedUser changes
  useEffect(() => {
    if (selectedUser) {
      setForm(mapApiToForm(selectedUser));
      setActiveTab("profile");
      setErrors({});
      setSaveState("idle");
    }
  }, [selectedUser]);

  if (!selectedUser) return null;

  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const toggleDay = (idx) => {
    setField(
      "workDays",
      form.workDays.includes(idx)
        ? form.workDays.filter((d) => d !== idx)
        : [...form.workDays, idx]
    );
  };

  // ── Validation ──────────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!form.firstName?.trim()) errs.firstName = "Required";
    if (!form.lastName?.trim()) errs.lastName = "Required";

    if (!form.phone?.trim()) {
      errs.phone = "Required";
    }

    if (!form.email?.trim()) {
      errs.email = "Required";
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      errs.email = "Invalid email";
    }

    if (!form.roleId) errs.roleId = "Required";
    return errs;
  };

  // ── Save ────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    console.log(1111);
    const errs = validate();
    console.log(errs);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setSaveState("saving");

    console.log(errs);

    try {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      // working_days_arr back to ["Mon","Tue",...] for API
      const workingDaysArr = form.workDays.map((i) => DAYS[i]);

      let roleLabel = "";
      if (form.roleId) {
        const sRole = roleList?.find((r) => r.id === form.roleId);
        if (sRole) {
          roleLabel = sRole.name;
        }
      }

      let payload = {
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        phone: form.phone,
        email: form.email.trim(),
        role: form.roleId,
        role_label: roleLabel,
        property: form.propertyIds,
        user_id: form.userId,
        app_user_id: form.appUserId,
        prev_role: form.prevRoleId,
        is_receive_emergency_call: form.isReceiveEmergencyCall,
        is_receive_tenant_emergency_call: form.isReceiveTenantEmergencyCall,
        is_hr_mode: form.hrEnabled ? 1 : 0,
        working_days: form.hrEnabled ? workingDaysArr : [],
        checkin_time: form.hrEnabled ? dayjsTo12h(form.shiftStart) : "",
        checkout_time: form.hrEnabled ? dayjsTo12h(form.shiftEnd) : "",
        checkin_reminder: form.hrEnabled ? form.clockInReminder : "",
        checkout_reminder: form.hrEnabled ? form.clockOutAlert : "",
        lunch_time: form.hrEnabled ? form.lunchTime : "",
        wage_type: form.hrEnabled ? form.wageType : "",
        wage: form.hrEnabled ? form.wageAmount : "",
        ot_wage_type: form.hrEnabled ? form.otWageType : "",
        ot_wage: form.hrEnabled ? form.otWageAmount : "",
      };

      if (payload?.phone) {
        console.log(payload?.phone);
        const phoneNumber = parsePhoneNumber(payload?.phone);

        console.log(phoneNumber);

        if (phoneNumber) {
          payload = { ...payload, country_code: phoneNumber?.country, isd_code: "+" + phoneNumber?.countryCallingCode, phone: phoneNumber?.nationalNumber };
        } else {
          payload = { ...payload, phone: payload?.phone.replace(/[^0-9]/g, ""), country_code: "US", isd_code: "+1" };
        }
      }

      if (payload?.wage) {
        payload = { ...payload, wage: payload?.wage?.toString().replace(/[^0-9]/g, "") };
      }

      if (payload?.ot_wage) {
        payload = { ...payload, ot_wage: payload?.ot_wage.toString().replace(/[^0-9]/g, "") };
      }

      const response = await axios.post(
        process.env.REACT_APP_USER_API_URL + `v1/users-new-phone/${form.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updatedUser = response.data.data;
      setSaveState("saved");
      onUserUpdate && onUserUpdate(updatedUser);

      // Log
      const logData = {
        title: "User is updated",
        description: [
          "Name: " + updatedUser.name,
          "Email: " + updatedUser.email,
          "Role: " + updatedUser.role,
        ],
      };
      AddLogCallAPI(logData, token);

      setTimeout(() => {
        setSaveState("idle");
        CloseTaskDetails && CloseTaskDetails();
      }, 1000);
    } catch (error) {
      console.log('error');
      console.log(error);
      setSaveState("idle");
      if (error.response) toast.error(error.response.data.message);
    }
  };

  // ── Derived display values ──────────────────────────────────────────────────
  const initials = (
    (form.firstName?.[0] ?? "") + (form.lastName?.[0] ?? "")
  ).toUpperCase() || "?";

  const previewFirstName = form.firstName || "User";
  const previewStart = form.shiftStart?.isValid?.()
    ? form.shiftStart.format("h:mm A")
    : "—";
  const previewText = `"Hi ${previewFirstName} — your shift starts at ${previewStart} today. Reply IN when you arrive. — PropertyDek"`;

  // Filter out "Any" option from propertyList for the select
  const selectableProperties = propertyList.filter((p) => p.value !== "all");

  return (
    <div className={`${styles.modal}`} role="dialog" aria-modal="true" aria-label="Edit user">

      {/* ── Header ── */}
      <div className={`${styles.mheader}`}>
        <div className={`${styles.avatarCircle}`}>
          {form.userImageUrl
            ? <img src={form.userImageUrl} alt={form.firstName} className={`${styles.avatarImg}`} />
            : initials
          }
        </div>
        <div className={`${styles.mhInfo}`}>
          <div className={`${styles.mhName}`}>
            {`${form.firstName} ${form.lastName}`.trim() || "—"}
            <span className={`${styles.modeBadge} ${styles.modeBadgeEdit}`}>editing</span>
          </div>
          <div className={`${styles.mhSub}`}>{form.propertyName || "—"}</div>
          <span className={`${styles.rolePill}`}>{form.role || "—"}</span>
        </div>
        <button className={`${styles.closeBtn}`} onClick={CloseTaskDetails} aria-label="Close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* ── Tabs ── */}
      <div className={`${styles.tabs}`} role="tablist">
        {["profile", "hr", "activity"].map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={activeTab === t}
            className={`${styles.tab} ${activeTab === t ? styles.tabActive : ""}`}
            onClick={() => setActiveTab(t)}
          >
            {t === "profile" && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 5, verticalAlign: -2 }} aria-hidden="true">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
              </svg>
            )}
            {t === "hr" && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 5, verticalAlign: -2 }} aria-hidden="true">
                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
              </svg>
            )}
            {t === "activity" && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 5, verticalAlign: -2 }} aria-hidden="true">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
            )}
            {t === "profile" ? "Profile" : t === "hr" ? "HR & schedule" : "Activity"}
          </button>
        ))}
      </div>

      {/* ── Body ── */}
      <div className={`${styles.body}`}>

        {/* PROFILE TAB */}
        {activeTab === "profile" && (
          <div className={`${styles.panel}`}>
            <div className={`${styles.sectionLabel}`}>Identity &amp; photo</div>

            {/* Stripe Identity row */}
            <div className={`${styles.stripeRow}`}>
              <div className={`${styles.stripeIcon}`}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" aria-hidden="true">
                  <rect x="3" y="5" width="18" height="14" rx="2" /><line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </div>
              <div className={`${styles.stripeText}`}>
                <div className={`${styles.stripeName}`}>Stripe Identity</div>
                <div className={`${styles.stripeSub}`}>Photo pulled from government ID scan</div>
              </div>
              {form.verified ? (
                <span className={`${styles.verifiedBadge}`}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Verified
                </span>
              ) : (
                <button
                  className={`${styles.verifyStripeBtn}`}
                  type="button"
                // onClick={() => onSendVerification && onSendVerification(form.id)}
                >
                  Verify now
                </button>
              )}
            </div>

            {/* Form grid */}
            <div className={`${styles.formGrid}`}>
              <div className={`${styles.fieldGroup}`}>
                <div className="FormGroup">
                  <TextField
                    fullWidth
                    label="First Name*"
                    value={form.firstName || ""}
                    onChange={(e) => setField("firstName", e.target.value)}
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                  />
                </div>
              </div>
              <div className={`${styles.fieldGroup}`}>
                <div className="FormGroup">
                  <TextField
                    fullWidth
                    label="Last Name*"
                    value={form.lastName || ""}
                    onChange={(e) => setField("lastName", e.target.value)}
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                  />
                </div>
              </div>
              <div className={`${styles.fieldGroup}`}>
                <div className="FormGroup">
                  <PhoneInput
                    international
                    countryCallingCodeEditable={false}
                    value={form.phone || ""}
                    onChange={(e) => setField("phone", e)}
                    inputComponent={PhoneTextField}
                  />
                </div>
              </div>
              <div className={`${styles.fieldGroup}`}>
                <div className="FormGroup">
                  <TextField
                    fullWidth
                    label="Email"
                    value={form.email || ""}
                    onChange={(e) => setField("email", e.target.value)}
                    error={!!errors.email}
                    helperText={errors.email}
                  />
                </div>
              </div>
              <div className={`${styles.fieldGroup}`}>
                <div className="FormGroup">
                  <FormControl fullWidth error={!!errors.roleId}>
                    <InputLabel>Role*</InputLabel>
                    <Select
                      label="Role*"
                      value={form.roleId || ""}
                      onChange={(e) => {
                        const selected = roleList.find((r) => r.id === e.target.value);
                        setField("roleId", e.target.value);
                        setField("role", selected?.name || "");
                      }}
                    >
                      {roleList.map((r) => (
                        <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                      ))}
                    </Select>
                    {errors.roleId && (
                      <span className={`${styles.fieldError}`}>{errors.roleId}</span>
                    )}
                  </FormControl>
                </div>
              </div>
              <div className={`${styles.fieldGroup}`}>
                <div className="FormGroup">
                  <FormControl fullWidth>
                    <InputLabel>Property</InputLabel>
                    <Select
                      label="Property"
                      multiple
                      value={form.propertyIds || []}
                      onChange={(e) => {
                        const ids = e.target.value;
                        setField("propertyIds", ids);
                        // Update display name to first selected
                        const first = selectableProperties.find((p) => p.value === ids[0]);
                        setField("propertyName", first?.label || "");
                      }}
                      renderValue={(selected) => {
                        const names = selected.map(
                          (id) => selectableProperties.find((p) => p.value === id)?.label || id
                        );
                        return names.join(", ");
                      }}
                    >
                      {selectableProperties.map((p) => (
                        <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* HR & SCHEDULE TAB */}
        {activeTab === "hr" && (
          <div className={`${styles.panel}`}>
            {/* HR Mode toggle */}
            <div className={`${styles.hrToggleRow}`}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#534AB7" strokeWidth="1.8" aria-hidden="true">
                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
              </svg>
              <div className={`${styles.hrToggleText}`}>
                <div className={`${styles.hrToggleTitle}`}>Enable HR mode</div>
                <div className={`${styles.hrToggleSub}`}>Enables SMS clock-in/out and shift reminders</div>
              </div>
              <button
                type="button"
                className={`${styles.toggle} ${form.hrEnabled ? styles.toggleOn : ""}`}
                onClick={() => setField("hrEnabled", !form.hrEnabled)}
                aria-label="Toggle HR mode"
                aria-pressed={form.hrEnabled}
              >
                <div className={`${styles.toggleThumb}`} />
              </button>
            </div>

            {form.hrEnabled ? (
              <>
                <div className={`${styles.sectionLabel}`} style={{ marginTop: 20 }}>Work schedule</div>

                {/* Day chips */}
                <div className={`${styles.dayRow}`}>
                  {DAYS.map((d, i) => (
                    <button
                      key={d}
                      type="button"
                      className={`${styles.dayChip} ${form.workDays.includes(i) ? styles.dayChipActive : ""}`}
                      onClick={() => toggleDay(i)}
                      aria-pressed={form.workDays.includes(i)}
                    >
                      {d}
                    </button>
                  ))}
                </div>

                {/* Time pickers */}
                <div className={`${styles.timeGrid}`}>
                  <div className={`${styles.wrapper} FormGroup`}>
                    <span className={`${styles.label}`}>Shift start</span>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <TimePicker
                        value={form.shiftStart || null}
                        onChange={(val) => setField("shiftStart", val)}
                        slotProps={{ textField: { fullWidth: true } }}
                      />
                    </LocalizationProvider>
                  </div>
                  <div className={`${styles.wrapper} FormGroup`}>
                    <span className={`${styles.label}`}>Shift end</span>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <TimePicker
                        value={form.shiftEnd || null}
                        onChange={(val) => setField("shiftEnd", val)}
                        slotProps={{ textField: { fullWidth: true } }}
                      />
                    </LocalizationProvider>
                  </div>
                </div>

                <div className={`${styles.sectionLabel}`} style={{ marginTop: 20 }}>SMS reminders</div>

                <div className={`${styles.smsGrid}`}>
                  <div className={`${styles.fieldGroup}`}>
                    <div className="FormGroup">
                      <FormControl fullWidth>
                        <InputLabel>Clock-in reminder</InputLabel>
                        <Select
                          label="Clock-in reminder"
                          value={form.clockInReminder || ""}
                          onChange={(e) => setField("clockInReminder", e.target.value)}
                        >
                          <MenuItem value={'5 minutes'}>5 min before shift</MenuItem>
                          <MenuItem value={'15 minutes'}>15 min before shift</MenuItem>
                          <MenuItem value={'30 minutes'}>30 min before shift</MenuItem>
                          <MenuItem value={'1 hour'}>1 hr before shift</MenuItem>
                        </Select>
                      </FormControl>
                    </div>
                  </div>
                  <div className={`${styles.fieldGroup}`}>
                    <div className="FormGroup">
                      <FormControl fullWidth>
                        <InputLabel>Missed clock-out alert</InputLabel>
                        <Select
                          label="Missed clock-out alert"
                          value={form.clockOutAlert || ""}
                          onChange={(e) => setField("clockOutAlert", e.target.value)}
                        >
                          <MenuItem value={'30 minutes'}>30 min after shift end</MenuItem>
                          <MenuItem value={'1 hour'}>1 hr after shift end</MenuItem>
                          <MenuItem value={'2 hours'}>2 hrs after shift end</MenuItem>
                        </Select>
                      </FormControl>
                    </div>
                  </div>
                  <div className={`${styles.fieldGroup}`}>
                    <div className="FormGroup">
                      <FormControl fullWidth>
                        <InputLabel id="demo-simple-select-label">Lunch Break</InputLabel>
                        <Select
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          label="Lunch Break"
                          value={form.lunchTime || ""}
                          onChange={(e) => setField("lunchTime", e.target.value)}
                        >
                          <MenuItem value={"15 minutes"}>15 Min</MenuItem>
                          <MenuItem value={"30 minutes"}>30 Min</MenuItem>
                          <MenuItem value={"45 minutes"}>45 Min</MenuItem>
                          <MenuItem value={"1 hour"}>1 Hour</MenuItem>
                        </Select>
                      </FormControl>
                    </div>
                  </div>
                </div>

                <div className={`${styles.fieldGroup} ${styles.fieldGroupFull}`}>
                  <div className={`${styles.sectionLabel}`} style={{ marginTop: 8, marginBottom: 10 }}>Compensation</div>

                  {/* Normal wage row */}
                  <div className={`${styles.wageSubLabel}`}>Normal wage</div>
                  <div className={`${styles.wageRow}`}>
                    <div className="FormGroup" style={{ flex: "0 0 140px" }}>
                      <FormControl fullWidth>
                        <InputLabel>Wage type</InputLabel>
                        <Select
                          label="Wage type"
                          value={form.wageType || "hourly"}
                          onChange={(e) => setField("wageType", e.target.value)}
                        >
                          <MenuItem value={"hourly"}>Hourly</MenuItem>
                        </Select>
                      </FormControl>
                    </div>
                    <div className="FormGroup" style={{ flex: 1 }}>
                      <TextField
                        fullWidth
                        label={`Amount (/ hr)`}
                        value={form.wageAmount || ""}
                        onChange={(e) => setField("wageAmount", e.target.value)}
                        placeholder="0.00"
                        InputProps={{
                          startAdornment: <span style={{ marginRight: 0, color: "#888", marginLeft: 12 }}>$</span>
                        }}
                      />
                    </div>
                  </div>

                  {/* OT wage row */}
                  <div className={`${styles.wageSubLabel}`} style={{ marginTop: 12 }}>Overtime (OT) wage</div>
                  <div className={`${styles.wageRow}`}>
                    <div className="FormGroup" style={{ flex: "0 0 140px" }}>
                      <FormControl fullWidth>
                        <InputLabel>OT wage type</InputLabel>
                        <Select
                          label="OT wage type"
                          value={form.otWageType || "hourly"}
                          onChange={(e) => setField("otWageType", e.target.value)}
                        >
                          <MenuItem value={"hourly"}>Hourly</MenuItem>
                        </Select>
                      </FormControl>
                    </div>
                    <div className="FormGroup" style={{ flex: 1 }}>
                      <TextField
                        fullWidth
                        label={`OT amount (/ hr)`}
                        value={form.otWageAmount || ""}
                        onChange={(e) => setField("otWageAmount", e.target.value)}
                        placeholder="0.00"
                        InputProps={{
                          startAdornment: <span style={{ marginRight: 0, color: "#888", marginLeft: 12 }}>$</span>
                        }}
                      />
                    </div>
                  </div>
                </div>

                {(form.wageAmount || form.otWageAmount) && (
                  <div className={`${styles.wageSummaryWrap}`}>

                    {/* Normal wage card */}
                    {form.wageAmount && (<div className={`${styles.wageSummaryCard}`}>
                      <div className={`${styles.wageSummaryIcon}`}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                        </svg>
                      </div>
                      <div className={`${styles.wageSummaryInfo}`}>
                        <div className={`${styles.wageSummaryLabel}`}>Normal wage</div>
                        <div className={`${styles.wageSummaryValue}`}>
                          ${parseFloat(form.wageAmount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          <span className={`${styles.wageSummaryUnit}`}>/ hr</span>
                        </div>
                      </div>
                      <div className={`${styles.wageSummaryType}`}>Hourly</div>
                    </div>)}

                    {/* OT wage card */}
                    {form.otWageAmount && (<div className={`${styles.wageSummaryCard} ${styles.wageSummaryCardOt}`}>
                      <div className={`${styles.wageSummaryIcon} ${styles.wageSummaryIconOt}`}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                        </svg>
                      </div>
                      <div className={`${styles.wageSummaryInfo}`}>
                        <div className={`${styles.wageSummaryLabel} ${styles.wageSummaryLabelOt}`}>Overtime (OT) wage</div>
                        <div className={`${styles.wageSummaryValue} ${styles.wageSummaryValueOt}`}>
                          ${parseFloat(form.otWageAmount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          <span className={`${styles.wageSummaryUnit} ${styles.wageSummaryUnitOt}`}>/ hr</span>
                        </div>
                      </div>
                      <div className={`${styles.wageSummaryType} ${styles.wageSummaryTypeOt}`}>Hourly</div>
                    </div>)}

                  </div>)}

                {/* Reminder preview */}
                <div className={`${styles.reminderPreview}`}>
                  <div className={`${styles.previewLabel}`}>Reminder preview</div>
                  <div className={`${styles.previewText}`}>{previewText}</div>
                </div>
              </>
            ) : (
              /* HR disabled empty state */
              <div className={`${styles.panel} ${styles.panelEmpty}`}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: "#ccc", marginBottom: 10 }} aria-hidden="true">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                  <line x1="4" y1="4" x2="20" y2="20" />
                </svg>
                <div className={`${styles.emptyTitle}`}>HR mode is off</div>
                <div className={`${styles.emptySub}`}>
                  Enable HR mode to track work schedules,<br />
                  clock-ins, and send SMS shift reminders.
                </div>
              </div>
            )}
          </div>
        )}

        {/* ACTIVITY TAB */}
        {activeTab === "activity" && (
          <div className={`${styles.panel} ${styles.panelEmpty}`}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: "#ccc", marginBottom: 10 }} aria-hidden="true">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
            <div className={`${styles.emptyTitle}`}>No activity yet</div>
            <div className={`${styles.emptySub}`}>Clock-ins, schedule changes, and system events will appear here</div>
          </div>
        )}

      </div>

      {/* ── Footer ── */}
      <div className={`${styles.footer}`}>
        <button
          className={`${styles.archiveBtn}`}
          type="button"
          onClick={() => onArchive && onArchive(form.id, form.isArchived)}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" /><line x1="10" y1="12" x2="14" y2="12" />
          </svg>
          {form.isArchived ? "Unarchive user" : "Archive user"}
        </button>
        <div className={`${styles.footerSpacer}`} />
        <button className={`${styles.cancelBtn}`} type="button" onClick={CloseTaskDetails}>
          Cancel
        </button>
        <button
          className={`${styles.saveBtn} ${saveState === "saved" ? styles.saveBtnSaved : ""}`}
          type="button"
          onClick={handleSave}
          disabled={saveState === "saving"}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          {saveState === "saving" ? "Saving…" : saveState === "saved" ? "Saved!" : "Save changes"}
        </button>
      </div>

    </div>
  );
}